import { resolveSeat } from './seatPerspective';

const SYNC_RETRY_DELAYS = [3000, 10000];
const GUEST_JOIN_BACKOFF_MS = 2000;

/**
 * Orchestrates a match's connection handshake: connect / create / join /
 * spectate / resume, seat resolution, socket wiring, and state-sync. Extracted
 * from GameBoard.initSession so the connection logic is unit-testable.
 *
 * Scope is the handshake only. The post-join game setup (deck spawn vs.
 * restore dispatch, draft-round blob restore, dice/opening-draw gating) stays
 * in GameBoard. It is entangled with non-connection concerns and runs after
 * start() resolves. Solo/offline sessions never reach here (GameBoard handles
 * the 'solo' kind itself).
 *
 * No DOM, no WebGL, no React. Injected dependencies:
 *   - transport: socketClient functions (createRoomWithCode, joinRoom,
 *     spectateRoom, requestStateSync).
 *   - host: a semantic adapter implemented by GameBoard. The orchestrator
 *     expresses intent ("the room is ready", "apply this seat"); the adapter
 *     translates that to setState / Three.js. Seat is resolved here and is the
 *     single source of truth, applied through host.applySeatPerspective. The
 *     single funnel for every seat-dependent scene write.
 *   - getProps: () => current GameBoard props.
 *   - now / setTimer: injectable clock + scheduler for tests.
 */
export class MatchConnection {
  constructor({ transport, host, getProps, now = () => Date.now(), setTimer = (fn, ms) => setTimeout(fn, ms) }) {
    this.transport = transport;
    this.host = host;
    this.getProps = getProps;
    this.now = now;
    this._setTimer = setTimer;
    this._lastSyncAt = 0;
    this._aborted = false;
    this._wakeSleep = null;
  }

  async start() {
    const props = this.getProps();
    const seat = resolveSeat({ sessionMode: props.sessionMode, props });

    switch (seat.kind) {
      case 'host-new':
        return this._startHost(props, { loadingMessage: 'Creating match room...', resume: false });
      case 'host-resume':
        return this._startHost(props, { loadingMessage: 'Restoring match...', resume: true });
      case 'spectate':
        return this._startSpectate(props);
      case 'replay':
        return this._startReplay(props);
      case 'guest-join':
      case 'guest-resume':
        return this._startGuest(props);
      default:
        return undefined; // 'solo'. GameBoard handles it without a connection.
    }
  }

  async _startHost(props, { loadingMessage, resume }) {
    this.host.connecting(loadingMessage);
    try {
      const roomInfo = await this.transport.createRoomWithCode(props.joinRoomCode);
      const opponentPresent = !!roomInfo.opponentInRoom;
      this.host.roomReady({
        roomCode: roomInfo.roomCode,
        isHost: true,
        roomInfo,
        connectionStatus: opponentPresent ? 'connected' : 'waiting',
        matchStartTime: opponentPresent ? this.now() : null,
      });
      this.host.persistActiveSession({ roomCode: roomInfo.roomCode, isHost: true, isArenaMatch: true });
      this.host.wireSocket();

      if (resume) {
        // The server's actor is the ONLY resume source: pull the per-seat
        // Full and let the mirror fold rebuild the board. No local-blob
        // optimistic base, no peer seeding. Both were legacy-P2P paths
        // whose stale state the authoritative Full then had to out-race.
        this.requestFullWithRetry(roomInfo.roomCode);
      }

      this.host.applySeatPerspective(true);
    } catch (error) {
      this.host.connectionFailed({ error });
    }
  }

  async _startSpectate(props) {
    this.host.connecting('Joining as spectator...');
    try {
      const roomInfo = await this.transport.spectateRoom(props.joinRoomCode);
      this.host.roomReady({
        roomCode: roomInfo.roomCode,
        isHost: false,
        roomInfo,
        connectionStatus: 'connected',
        loadingMessage: 'Watching match...',
      });
      this.host.wireSocket();
      // Spectators keep the default (host-facing) orientation, so no
      // applySeatPerspective here. They pull the board via the spectator-
      // projected match:full the server serves seatless audience members.
      this.requestFullWithRetry(roomInfo.roomCode);
    } catch (error) {
      this.host.connectionFailed({ error });
    }
  }

  // Replay playback: spectate minus the transport. The room is synthesized as
  // ready so the spectator mount path proceeds, and wireSocket hands the fold
  // funnel to the ReplayDriver (setupSocketListeners' replay branch), whose
  // initial Full populates the board. persistActiveSession is deliberately not
  // called: a replay must never register as a live session.
  _startReplay(props) {
    // The chosen viewpoint IS the seat: viewing as p2 mounts the board the
    // way p2 saw it (camera rotated, their hand at the bottom), through the
    // same seat funnel live matches use.
    const isHost = props.replayViewpoint !== 'p2';
    this.host.roomReady({
      roomCode: props.joinRoomCode || 'replay',
      isHost,
      roomInfo: {},
      connectionStatus: 'connected',
      loadingMessage: 'Loading replay...',
    });
    this.host.wireSocket();
    this.host.applySeatPerspective(isHost);
  }

  async _startGuest(props) {
    this._aborted = false;
    const maxRetries = props.isArenaMatch ? 15 : 1;
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      if (this._aborted || this._isUnmounted()) return;
      this.host.connecting(
        attempt > 0 ? `Connecting to host (attempt ${attempt + 1})...` : 'Connecting to host...',
        { canCancel: attempt > 0 },
      );
      try {
        const roomInfo = await this.transport.joinRoom(props.joinRoomCode);
        if (this._aborted) return;
        const isHost = !!roomInfo.isHost;
        this.host.roomReady({
          roomCode: roomInfo.roomCode,
          isHost,
          roomInfo,
          connectionStatus: 'connected',
          loadingMessage: 'Preparing battlefield...',
          canCancelJoin: false,
        });
        this.host.persistActiveSession({ roomCode: roomInfo.roomCode, isHost, isArenaMatch: !!props.isArenaMatch });
        this.host.wireSocket();
        // Seat resolved from the server. Apply it before any post-join scene
        // work so the camera/badges never render from the optimistic default.
        this.host.applySeatPerspective(isHost);
        // A resumed guest rebuilds its board from the authoritative Full,
        // same as the host-resume path. Draft rounds have a second durable
        // resume signal: `draft:match_start.resumed` becomes isResumeJoin.
        // Save & Leave deliberately keeps the room alive but removes both
        // active members, so the subsequent room handshake is `room:joined`
        // (resumed=false) even though the round is already in progress. Trust
        // either source; otherwise GameBoard skips fresh spawning yet never
        // pulls a Full, leaving an empty board.
        if (roomInfo.resumed || props.isResumeJoin) {
          this.requestFullWithRetry(roomInfo.roomCode);
        }
        return;
      } catch (error) {
        if (this._aborted) return;
        // 'Room not found' is terminal: the room died and the server has
        // parked the instance as paused, so no retry can ever succeed.
        // the match continues via the saved-games resume flow instead.
        const fatal = /room not found/i.test(error?.message || '');
        if (!fatal && attempt < maxRetries - 1) {
          await this._sleep(GUEST_JOIN_BACKOFF_MS);
          if (this._aborted) return;
          // Drop the socket before retrying so the next joinRoom reconnects clean.
          this.transport.disconnectSocket?.();
          continue;
        }
        this.host.connectionFailed({ error, final: true });
        return;
      }
    }
  }

  // Cancel an in-flight guest join retry loop (the "Cancel" button). Wakes any
  // pending backoff sleep so the loop exits promptly.
  abortJoin() {
    this._aborted = true;
    if (this._wakeSleep) this._wakeSleep();
  }

  // Request the authoritative Full from the server now, then retry a couple
  // times in case the socket is briefly slow. Any arriving Full
  // (noteAuthoritativeStateArrived) short-circuits the pending retries.
  requestFullWithRetry(roomCode) {
    if (this._isUnmounted()) return;
    const startedAt = this.now();
    this.host.pullAuthoritativeState?.(roomCode);
    for (const delay of SYNC_RETRY_DELAYS) {
      this._setTimer(() => {
        if (this._isUnmounted()) return;
        if (this._lastSyncAt >= startedAt) return;
        this.host.pullAuthoritativeState?.(roomCode);
      }, delay);
    }
  }

  noteAuthoritativeStateArrived() {
    this._lastSyncAt = this.now();
  }

  _isUnmounted() {
    return !!(this.host.isUnmounted && this.host.isUnmounted());
  }

  _sleep(ms) {
    return new Promise((resolve) => {
      this._wakeSleep = resolve;
      this._setTimer(() => {
        this._wakeSleep = null;
        resolve();
      }, ms);
    });
  }
}
